import { useCallback, useEffect, useState } from 'react';
import { peopleGateway } from '../../index';
import type { TaxSetupStatus } from '../../../tax-settings/types';

export interface AdminTaxSetupState {
  data: TaxSetupStatus | null;
  /** True when the read failed; the panel says nothing is shown as complete and offers Retry. */
  failed: boolean;
  reload: () => void;
}

/** The administrator's derived tax setup status for one employee (GET /api/employees/{id}/tax-setup), read once per Step 2 visit and again when a reciprocity
 * decision changes it. Never throws: a failure is `failed` so the panel can say so and offer Retry. */
export function useAdminTaxSetup(employeeId: string): AdminTaxSetupState {
  const [data, setData] = useState<TaxSetupStatus | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    peopleGateway
      .getEmployeeTaxSetup(employeeId)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setFailed(false);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId, attempt]);

  const reload = useCallback(() => {
    setFailed(false);
    setAttempt((n) => n + 1);
  }, []);

  return { data, failed, reload };
}
